import type { Granularity } from './buckets';

/**
 * How a chart's columns are named (CR-33, CR-34): a short label under the axis and a full title in
 * the hover text, for a day, a week (Saturday to Friday) or a month.
 *
 * Calendar dates throughout, read in UTC, never converted: the server has already said which Cairo
 * day each figure belongs to. Western digits in Arabic too (ASM-004).
 */

interface Span {
  readonly start: string;
  readonly end: string;
}

function dateFormat(locale: string, options: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC', numberingSystem: 'latn' });
}

function asDate(isoDate: string): Date {
  return new Date(`${isoDate}T00:00:00Z`);
}

/** Under the column: `11 Oct`, or `Oct` for a month. */
export function bucketLabel(span: Span, granularity: Granularity, locale: string): string {
  if (granularity === 'month') {
    return dateFormat(locale, { month: 'short' }).format(asDate(span.start));
  }
  return dateFormat(locale, { day: 'numeric', month: 'short' }).format(asDate(span.start));
}

/** In the hover text: `Sun, 11 Oct`, a week as `weekOf(first, last)`, or `October 2026`. */
export function bucketTitle(
  span: Span,
  granularity: Granularity,
  locale: string,
  weekOf: (first: string, last: string) => string,
): string {
  if (granularity === 'month') {
    return dateFormat(locale, { month: 'long', year: 'numeric' }).format(asDate(span.start));
  }
  const day = dateFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
  if (granularity === 'week') {
    return weekOf(day.format(asDate(span.start)), day.format(asDate(span.end)));
  }
  return day.format(asDate(span.start));
}
